import { t } from "i18next";
import { z } from "zod";

import languageCodeList from "../../constants/languages";

import type {
  CastingTranslation,
  CastingTranslationToCreate,
  CreateCastingTranslationResponse,
} from "../../api/MediaAPI.types";
import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { Picture, Pictures, TranslationsIndexes } from "../../types";

export const castingTranslationSchema = z.object({
  language: z.enum(languageCodeList),
  language_uuid: z.string().optional(),
  default: z.boolean(),
  uuid: z.string().optional(),
  name: z
    .string()
    .min(1, t("form:required", { field: t("form:fields.title") })),
  biography: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? ""),
  signature: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? ""),
  picture: z.custom<Picture>().nullable().optional(),
  pictures: z.custom<Pictures>().nullable().optional(),
});

export const castingTranslationsFormSchema = z.object({
  translations: z.array(castingTranslationSchema),
});

export interface CastingTranslationFormField {
  language: LanguageCode;
  language_uuid?: string;
  default: boolean;
  uuid?: string;
  name: string;
  biography: string;
  signature: string;
  picture?: Picture | null;
  pictures?: Pictures | null;
}

/**
 * Converts API translation objects to the form field format used by React Hook Form.
 */
export function apiToFormTranslations(
  translations: CastingTranslation[],
): CastingTranslationFormField[] {
  return [...translations]
    .sort((a, b) => {
      if (a.default) return -1;
      if (b.default) return 1;
      return a.language.localeCompare(b.language);
    })
    .map((translation) => ({
      language: translation.language as LanguageCode,
      language_uuid: translation.language_uuid,
      default: translation.default,
      uuid: translation.uuid,
      name: translation.name ?? "",
      biography: translation.biography ?? "",
      signature: translation.signature ?? "",
      picture: translation.picture ?? null,
      pictures: translation.pictures ?? null,
    }));
}

/**
 * Adapts a CreateCastingTranslationResponse into the CastingTranslationFormField shape.
 */
export function createTranslationResponseToFormField(
  response: CreateCastingTranslationResponse,
  language: LanguageCode,
): CastingTranslationFormField {
  return {
    uuid: response.uuid,
    default: response.default,
    language_uuid: response.language_uuid,
    name: response.name ?? "",
    biography: response.biography ?? "",
    signature: response.signature ?? "",
    language,
    picture: null,
    pictures: null,
  };
}

/**
 * Converts form field values back to the API shape.
 */
export function formToApiTranslations(
  formTranslations: CastingTranslationFormField[],
): {
  translations: CastingTranslationToCreate[];
  translations_indexes: TranslationsIndexes;
} {
  const translations: CastingTranslationToCreate[] = formTranslations.map(
    (field) => ({
      uuid: field.uuid,
      default: field.default,
      biography: field.biography,
      signature: field.signature,
      language: field.language,
      language_uuid: field.language_uuid ?? "",
      name: field.name,
      picture: field.picture,
      pictures: field.pictures,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const language_uuid: Record<string, number[]> = {};
  let defaultIndex = 0;

  formTranslations.forEach((field, index) => {
    if (field.language_uuid) {
      language_uuid[field.language_uuid] = [index];
    }
    if (field.default) {
      defaultIndex = index;
    }
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const translations_indexes: TranslationsIndexes = {
    language_uuid,
    default: { true: [defaultIndex] },
  };

  return { translations, translations_indexes };
}
